"use client";

import { Children, Fragment, isValidElement, useEffect, useRef, type ReactNode } from "react";
import styles from "./ReadingText.module.css";
import { READING_STAGGER, readingProgress, readingFadeAmount } from "./readingTextMotion";

type Word = { node: ReactNode; space: boolean };
type Split = { words: Word[]; space: boolean };

/**
 * Découpe en mots. Les chaînes sont coupées aux espaces, un élément (<mark>,
 * <strong>…) compte pour un mot, et on descend dans les fragments JSX (sinon
 * un paragraphe écrit `<>…</>` compterait pour un seul mot). `space` dit si le
 * mot est précédé d'une espace dans le texte d'origine : sans ça, la
 * ponctuation collée à un élément (« <mark>vendre</mark>. ») se détacherait.
 */
function collect(children: ReactNode, split: Split): void {
  for (const child of Children.toArray(children)) {
    if (typeof child === "string" || typeof child === "number") {
      for (const token of String(child).split(/(\s+)/)) {
        if (!token) continue;
        if (/^\s+$/.test(token)) {
          split.space = true;
          continue;
        }
        split.words.push({ node: token, space: split.space && split.words.length > 0 });
        split.space = false;
      }
    } else if (isValidElement(child) && child.type === Fragment) {
      collect((child.props as { children?: ReactNode }).children, split);
    } else {
      split.words.push({ node: child, space: split.space && split.words.length > 0 });
      split.space = false;
    }
  }
}

function toWords(children: ReactNode): Word[] {
  const split: Split = { words: [], space: false };
  collect(children, split);
  return split.words;
}

export type ReadingTextProps = {
  children: ReactNode;
  /** Décalage entre deux mots en secondes (0,05 comme le site). */
  stagger?: number;
  as?: "p" | "div";
  className?: string;
};

/**
 * Texte qui « se lit » au scroll : chaque mot se révèle à son tour, au rythme
 * de la progression du paragraphe dans l'écran. Sans JavaScript ou avec
 * prefers-reduced-motion, le texte s'affiche normalement.
 */
export function ReadingText({ children, stagger = READING_STAGGER, as: Tag = "p", className }: ReadingTextProps) {
  const ref = useRef<HTMLElement>(null);
  const words = toWords(children);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const nodes = Array.from(element.querySelectorAll<HTMLElement>("[data-word]"));
    if (!nodes.length) return;
    let frame = 0;

    const update = () => {
      frame = 0;
      const rect = element.getBoundingClientRect();
      const progress = readingProgress(rect.top, rect.height, window.innerHeight);
      nodes.forEach((node, index) => {
        const opacity = readingFadeAmount(progress, index, nodes.length, { stagger });
        node.style.opacity = opacity > 0.999 ? "" : opacity.toFixed(3);
      });
    };
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      nodes.forEach((node) => {
        node.style.removeProperty("opacity");
      });
    };
  }, [stagger, words.length]);

  return (
    <Tag ref={ref as never} className={className}>
      {words.map((word, index) => (
        <Fragment key={index}>
          {word.space ? " " : null}
          <span className={styles.word} data-word="">
            {word.node}
          </span>
        </Fragment>
      ))}
    </Tag>
  );
}
